import { createSelector } from '@reduxjs/toolkit';

import { colord } from 'colord';
import type { BrandKit, BrandKitColor } from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import { type DeepReadonly, withStableEmptyArray } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

export function isLoading(state: RootState) {
    return state.brandKits.loading;
}

export function getTeamBrandKitsCount(state: RootState) {
    return state.brandKits.teamCount;
}

export function isBrandKitSelected(state: RootState) {
    return state.brandKits.selectedBrandKitId !== null;
}

type StoredBrandKit = DeepReadonly<BrandKit>;

const NO_BRAND_KITS: readonly StoredBrandKit[] = [];
const NO_BRAND_KIT_IDS: readonly number[] = [];
const NO_BRAND_KIT_COLORS: readonly DeepReadonly<BrandKitColor>[] = [];

export const getAllBrandKits = createSelector(
    (state: RootState) => state.brandKits?.list,
    (brandKits): readonly StoredBrandKit[] => {
        if (!brandKits) {
            return NO_BRAND_KITS;
        }
        return withStableEmptyArray(new PlainJsStoreORM(brandKits).map((brandKit) => brandKit));
    },
);

export function getBrandKitIdList(state: RootState): readonly number[] {
    const list = state.brandKits?.list;
    if (!list) {
        return NO_BRAND_KIT_IDS;
    }
    return withStableEmptyArray(list.allIds);
}

export function getBrandKitById(state: RootState, brandKitId: number): StoredBrandKit | undefined {
    const list = state.brandKits?.list;
    if (!list) {
        return undefined;
    }
    return new PlainJsStoreORM(list).getById(brandKitId);
}

export function getBrandKitColorsById(
    state: RootState,
    brandKitId: number | null,
): readonly DeepReadonly<BrandKitColor>[] {
    if (brandKitId === null) {
        return NO_BRAND_KIT_COLORS;
    }
    return getBrandKitById(state, brandKitId)?.colors ?? NO_BRAND_KIT_COLORS;
}

export const getBrandKitColorsInHex = createSelector(
    [(state: RootState, brandKitId: number | null) => getBrandKitColorsById(state, brandKitId)],
    (brandKitColors): readonly string[] =>
        withStableEmptyArray(
            brandKitColors.map((color) => {
                const rgb = `rgb(${color.r}, ${color.g}, ${color.b})`;
                return colord(rgb).toHex();
            }),
        ),
);
